/**
 * Products Service - Story 6.2: Create My Products Page
 * API client for product management endpoints
 * Service: Commerce Service (port 8083)
 */

import { microservicesClient } from '@/services/microservices/index';
import type { Product, PaginatedProductsResponse, ProductFilters, ProductStatus } from '../types/products.types';

const COMMERCE_SERVICE = 'commerce-service';
const API_BASE = '/api/v1/marketplace/products';

/**
 * Custom error class for products API errors
 */
export class ProductsApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly errorCode?: string
  ) {
    super(message);
    this.name = 'ProductsApiError';
  }
}

/**
 * Dung product data from API
 */
interface DungProduct {
  id: string;
  productName: string;
  availableQuantity: number;
  totalQuantity: number;
  [key: string]: any;
}

/**
 * Aggregated product quantities (sum of all batches for same product)
 */
interface AggregatedProductQty {
  productName: string;
  totalQuantity: number;
  availableQuantity: number;
}

/**
 * Build query string from filters
 * @param filters - Filter options
 * @returns Query string parameters
 */
function buildQueryString(filters: ProductFilters): string {
  const params = new URLSearchParams();

  // Add page and pageSize
  params.append('page', (filters.page - 1).toString()); // API uses 0-based indexing
  params.append('pageSize', filters.pageSize.toString());

  // Add search filter
  if (filters.search) {
    params.append('search', filters.search);
  }

  // Add status filter (only if not 'ALL')
  if (filters.status !== 'ALL') {
    params.append('status', filters.status);
  }

  // Add category filter if provided
  if (filters.category) {
    params.append('category', filters.category);
  }

  // Add sorting
  if (filters.sortBy) {
    params.append('sortBy', filters.sortBy);
    params.append('sortOrder', filters.sortOrder || 'desc');
  }

  return params.toString();
}

/**
 * Fetch seller's products with filters and pagination
 * GET /api/v1/marketplace/products
 *
 * @param filters - Filter options including pagination
 * @returns Paginated products response
 * @throws ProductsApiError on API failure
 *
 * @example
 * const response = await fetchProducts({
 *   status: 'ACTIVE',
 *   search: 'biogas',
 *   page: 1,
 *   pageSize: 20,
 *   sortBy: 'createdDate',
 *   sortOrder: 'desc'
 * });
 */
export async function fetchProducts(filters: ProductFilters): Promise<PaginatedProductsResponse> {
  try {
    console.log('[ProductsService] Fetching products with filters:', filters);

    // Backend returns Spring Page object directly (not wrapped)
    // Always fetch ALL products (no status filter to API) so client-side
    // filtering can properly categorize by both status AND quantity thresholds.
    // Products with qty < 20 should appear in INACTIVE regardless of DB status.
    const response = await microservicesClient.get<{
      content: Product[];
      pageable: {
        pageNumber: number;
        pageSize: number;
      };
      totalElements: number;
      totalPages: number;
      number: number;
      size: number;
      first: boolean;
      last: boolean;
      empty: boolean;
    }>(COMMERCE_SERVICE, API_BASE, { params: {
      page: (filters.page - 1).toString(),
      pageSize: filters.pageSize.toString(),
      ...(filters.search && { search: filters.search }),
      ...(filters.category && { category: filters.category }),
      ...(filters.sortBy && { sortBy: filters.sortBy, sortOrder: filters.sortOrder || 'desc' }),
    } });

    // Check for API error response
    if (!response.success || !response.data) {
      console.error('[ProductsService] API returned error:', {
        success: response.success,
        status: response.status,
        error: response.error,
      });

      // Handle specific HTTP status codes
      if (response.status === 403) {
        throw new ProductsApiError(
          response.error || 'Forbidden - No permission to access products',
          403,
          'FORBIDDEN'
        );
      }
      if (response.status === 401) {
        throw new ProductsApiError(
          response.error || 'Unauthorized - Please login again',
          401,
          'UNAUTHORIZED'
        );
      }
      if (response.status === 404) {
        throw new ProductsApiError(
          response.error || 'Products not found',
          404,
          'NOT_FOUND'
        );
      }

      throw new ProductsApiError(
        response.error || 'Failed to fetch products',
        response.status || 500,
        'FETCH_ERROR'
      );
    }

    // Transform Spring Page format to our PaginatedProductsResponse format
    const transformed: PaginatedProductsResponse = {
      data: response.data.content || [],
      pagination: {
        page: (response.data.number || 0) + 1, // Convert from 0-based to 1-based
        pageSize: response.data.size || 20,
        totalPages: response.data.totalPages || 0,
        totalItems: response.data.totalElements || 0,
        hasNext: !response.data.last,
        hasPrev: !response.data.first,
      },
    };

    console.log('[ProductsService] Successfully fetched products:', {
      count: transformed.data.length,
      totalPages: transformed.pagination.totalPages,
      totalItems: transformed.pagination.totalItems,
      firstProductImages: transformed.data[0]?.images,
    });

    return transformed;
  } catch (error) {
    console.error('[ProductsService] Error fetching products:', error);

    if (error instanceof ProductsApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('404')) {
        throw new ProductsApiError('Products not found', 404, 'NOT_FOUND');
      }
      if (error.message.includes('401')) {
        throw new ProductsApiError('Unauthorized - Please login again', 401, 'UNAUTHORIZED');
      }
      if (error.message.includes('403')) {
        throw new ProductsApiError('Forbidden - No permission to access products', 403, 'FORBIDDEN');
      }
      throw new ProductsApiError(error.message, 500, 'FETCH_ERROR');
    }

    throw new ProductsApiError('Unknown error fetching products', 500, 'UNKNOWN_ERROR');
  }
}

/**
 * Fetch single product by ID
 * GET /api/v1/marketplace/products/{id}
 *
 * @param productId - Product ID to fetch
 * @returns Product details
 * @throws ProductsApiError on API failure
 */
export async function fetchProductById(productId: string): Promise<Product> {
  try {
    console.log('[ProductsService] Fetching product by ID:', productId);

    const response = await microservicesClient.get<Product>(
      COMMERCE_SERVICE,
      `${API_BASE}/${productId}`
    );

    if (!response.success) {
      throw new ProductsApiError(
        response.error || 'Failed to fetch product',
        response.status
      );
    }

    console.log('[ProductsService] Successfully fetched product:', productId);

    return response.data;
  } catch (error) {
    console.error('[ProductsService] Error fetching product by ID:', error);

    if (error instanceof ProductsApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('404')) {
        throw new ProductsApiError('Product not found', 404, 'PRODUCT_NOT_FOUND');
      }
      throw new ProductsApiError(error.message, 500, 'FETCH_ERROR');
    }

    throw new ProductsApiError('Unknown error fetching product', 500, 'UNKNOWN_ERROR');
  }
}

/**
 * Delete a product
 * DELETE /api/v1/marketplace/products/{id}
 *
 * @param productId - Product ID to delete
 * @returns Success response
 * @throws ProductsApiError on API failure
 */
export async function deleteProduct(productId: string): Promise<{ success: boolean; message: string }> {
  try {
    console.log('[ProductsService] Deleting product:', productId);

    const response = await microservicesClient.delete<{ message: string }>(
      COMMERCE_SERVICE,
      `${API_BASE}/${productId}`
    );

    if (!response.success) {
      throw new ProductsApiError(
        response.error || 'Failed to delete product',
        response.status
      );
    }

    console.log('[ProductsService] Successfully deleted product:', productId);

    return {
      success: response.success,
      message: response.data?.message || 'Product deleted successfully',
    };
  } catch (error) {
    console.error('[ProductsService] Error deleting product:', error);

    if (error instanceof ProductsApiError) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.message.includes('404')) {
        throw new ProductsApiError('Product not found', 404, 'PRODUCT_NOT_FOUND');
      }
      if (error.message.includes('403')) {
        throw new ProductsApiError('Forbidden - you do not own this product', 403, 'FORBIDDEN');
      }
      throw new ProductsApiError(error.message, 500, 'DELETE_ERROR');
    }

    throw new ProductsApiError('Unknown error deleting product', 500, 'UNKNOWN_ERROR');
  }
}

/**
 * Toggle product status (ACTIVE <-> INACTIVE)
 * PATCH /api/v1/marketplace/products/{id}/status
 *
 * @param productId - Product ID
 * @param newStatus - New status (ACTIVE or INACTIVE)
 * @returns Updated product
 * @throws ProductsApiError on API failure
 */
export async function toggleProductStatus(
  productId: string,
  newStatus: ProductStatus
): Promise<Product> {
  try {
    console.log('[ProductsService] Toggling product status:', { productId, newStatus });

    const response = await microservicesClient.patch<Product>(
      COMMERCE_SERVICE,
      `${API_BASE}/${productId}/status`,
      { status: newStatus }
    );

    if (!response.success) {
      throw new ProductsApiError(
        response.error || 'Failed to update product status',
        response.status
      );
    }

    console.log('[ProductsService] Successfully updated product status:', productId);

    return response.data;
  } catch (error) {
    console.error('[ProductsService] Error toggling product status:', error);

    if (error instanceof ProductsApiError) {
      throw error;
    }

    if (error instanceof Error) {
      throw new ProductsApiError(error.message, 500, 'UPDATE_ERROR');
    }

    throw new ProductsApiError('Unknown error updating product status', 500, 'UNKNOWN_ERROR');
  }
}

/**
 * Get product statistics for seller
 * GET /api/v1/marketplace/products/stats
 *
 * @returns Product statistics
 * @throws ProductsApiError on API failure
 */
export async function fetchProductStats(): Promise<{
  totalProducts: number;
  activeProducts: number;
  inactiveProducts: number;
  lastUpdated: string | null;
}> {
  try {
    console.log('[ProductsService] Fetching product statistics');

    const response = await microservicesClient.get<{
      totalProducts: number;
      activeProducts: number;
      inactiveProducts: number;
      lastUpdated: string | null;
    }>(COMMERCE_SERVICE, `${API_BASE}/stats`);

    if (!response.success) {
      throw new ProductsApiError(
        response.error || 'Failed to fetch product statistics',
        response.status
      );
    }

    console.log('[ProductsService] Successfully fetched product statistics');

    return response.data;
  } catch (error) {
    console.error('[ProductsService] Error fetching product statistics:', error);

    if (error instanceof ProductsApiError) {
      throw error;
    }

    throw new ProductsApiError('Unknown error fetching product statistics', 500, 'STATS_ERROR');
  }
}

/**
 * Fetch available quantities from dung_products table
 * GET /api/v1/public/marketplace/dung-products
 *
 * @returns Map of product names (lowercase) to available quantities aggregated across all batches
 */
export async function fetchDungProductsAvailable(): Promise<Map<string, number>> {
  try {
    console.log('[ProductsService] Fetching dung_products for available quantities');

    const response = await microservicesClient.get<{
      content?: any[];
      data?: any[];
      [key: string]: any;
    }>(
      COMMERCE_SERVICE,
      '/api/v1/public/marketplace/dung-products',
      { params: { size: 1000, page: 0 } }
    );

    const availableMap = new Map<string, number>();
    let dungProducts: any[] = [];

    if (Array.isArray(response.data)) {
      dungProducts = response.data;
    } else if (response.data?.content && Array.isArray(response.data.content)) {
      dungProducts = response.data.content;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      dungProducts = response.data.data;
    }

    for (const product of dungProducts) {
      const productName = product.productName?.trim().toLowerCase() || '';
      if (!productName) continue;

      const qty = product.quantityAvailable || 0;
      const existing = availableMap.get(productName) || 0;
      availableMap.set(productName, existing + qty);
    }

    console.log('[ProductsService] ✅ Available quantities aggregated:', availableMap.size);
    return availableMap;
  } catch (error) {
    console.error('[ProductsService] Error fetching available quantities:', error);
    return new Map();
  }
}

/**
 * Fetch total quantities from production batches
 * GET /api/v1/mfg-production-batches/ready-for-sale (commerce-service)
 *
 * @returns Map of product names (lowercase) to total output quantities in kg
 */
export async function fetchProductionBatchQuantities(): Promise<Map<string, number>> {
  try {
    console.log('[ProductsService] Fetching production batches for total quantities');

    const response = await microservicesClient.get<{
      content?: any[];
      data?: any[];
      [key: string]: any;
    }>(
      'commerce-service',
      '/api/v1/mfg-production-batches/ready-for-sale',
      { params: { size: 1000, page: 0 } }
    );

    const totalMap = new Map<string, number>();
    let batches: any[] = [];

    if (Array.isArray(response.data)) {
      batches = response.data;
    } else if (response.data?.content && Array.isArray(response.data.content)) {
      batches = response.data.content;
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      batches = response.data.data;
    }

    for (const batch of batches) {
      const productName = batch.productName?.trim().toLowerCase() || '';
      if (!productName) continue;

      const qty = batch.output_quantity_kg || batch.outputQuantityKg || batch.outputQuantity || 0;
      const existing = totalMap.get(productName) || 0;
      totalMap.set(productName, existing + qty);
    }

    console.log('[ProductsService] ✅ Total quantities aggregated:', totalMap.size);
    return totalMap;
  } catch (error) {
    console.error('[ProductsService] Error fetching production batch quantities:', error);
    return new Map();
  }
}

/**
 * Fetch actual product quantities from dung_products table
 * Aggregates quantities for products with multiple batches
 * GET /api/v1/public/marketplace/dung-products
 *
 * @returns Map of product names to aggregated quantities
 */
export async function fetchDungProductQuantities(): Promise<Map<string, AggregatedProductQty>> {
  try {
    console.log('[ProductsService] Fetching dung_products for quantities');

    // Fetch from the dung_products API endpoint (public, no auth required)
    const response = await microservicesClient.get<{
      content?: DungProduct[];
      data?: DungProduct[];
      [key: string]: any;
    }>(
      COMMERCE_SERVICE,
      '/api/v1/public/marketplace/dung-products',
      {
        params: {
          size: 1000, // Get all products
          page: 0
        }
      }
    );

    console.log('[ProductsService] API Response details:', {
      success: response.success,
      status: response.status,
      dataType: typeof response.data,
      isArray: Array.isArray(response.data),
      dataKeys: response.data ? Object.keys(response.data) : null
    });

    const quantitiesMap = new Map<string, AggregatedProductQty>();

    // Extract products array from response - try multiple formats
    let dungProducts: any[] = [];

    if (Array.isArray(response.data)) {
      dungProducts = response.data;
      console.log('[ProductsService] Response is direct array');
    } else if (response.data?.content && Array.isArray(response.data.content)) {
      dungProducts = response.data.content;
      console.log('[ProductsService] Found products in "content" field');
    } else if (response.data?.data && Array.isArray(response.data.data)) {
      dungProducts = response.data.data;
      console.log('[ProductsService] Found products in "data" field');
    } else if (response.data?.dungProducts && Array.isArray(response.data.dungProducts)) {
      dungProducts = response.data.dungProducts;
      console.log('[ProductsService] Found products in "dungProducts" field');
    }

    console.log('[ProductsService] Got dung_products:', dungProducts.length, 'products');
    if (dungProducts.length > 0) {
      console.log('[ProductsService] First product sample:', JSON.stringify(dungProducts[0]));
    }

    // Aggregate quantities by product name (sum all batches for same product)
    for (const product of dungProducts) {
      const productName = product.productName?.trim().toLowerCase() || '';

      if (!productName) {
        console.warn('[ProductsService] Product missing name:', product);
        continue;
      }

      const existing = quantitiesMap.get(productName);

      if (existing) {
        // Sum quantities for products with multiple batches
        existing.totalQuantity += product.totalQuantity || 0;
        existing.availableQuantity += product.availableQuantity || 0;
        console.log(`[ProductsService] Aggregated ${productName}: total=${existing.totalQuantity}, available=${existing.availableQuantity}`);
      } else {
        // First batch of this product
        quantitiesMap.set(productName, {
          productName: product.productName,
          totalQuantity: product.totalQuantity || 0,
          availableQuantity: product.availableQuantity || 0,
        });
        console.log(`[ProductsService] Stored ${productName}: total=${product.totalQuantity}, available=${product.availableQuantity}`);
      }
    }

    console.log('[ProductsService] ✅ Dung products quantities aggregated:', quantitiesMap.size, 'unique products');
    console.log('[ProductsService] Final map keys:', Array.from(quantitiesMap.keys()));
    return quantitiesMap;
  } catch (error) {
    console.error('[ProductsService] Error fetching dung_products quantities:', error);
    // Return empty map on error - will fall back to other methods
    return new Map();
  }
}
